import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  BreadcrumbArray,
  Button,
  CurrencyAmountUI,
  DateUI,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  PrimaryFullScreenSpinner,
  Spinner,
  StatusUI,
  Txt,
  toast,
  UserProfile,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import Decimal from "decimal.js";
import type { FC } from "react";
import { Suspense, useCallback, useState } from "react";
import { DetailSubRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { PaneActions } from "~/components/operations/actions";
import { TransferGroupUI } from "~/components/TransferGroup/TransferGroupUI";
import { graphql } from "~/gql/client";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/transfer-groups_/$transferGroupId",
)({
  head: () => opsHead("Transfer Group"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { transferGroupId } = Route.useParams();
  if (!transferGroupId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminTransferGroupDetail($id: ID!) {
        node(id: $id) {
          ... on TransferGroup {
            id
            shortRef
            kind
            status
            createdAt
            expectedCompletionDate
            actualCompletionDate
            requestedForDate
            organisation {
              id
              ...OrganisationCardFields
            }
            createdByUser {
              ...UserProfileUI
            }
            totalVolume {
              currency
              amount
            }
            userReviewRequired
            approvalReviewRequiredByDate
            approvalRequest {
              id
              result
              currentApprovedCount
              approvalPolicy {
                id
                numUserReviewsRequired
              }
              currentApprovalReviews {
                id
                decision
                createdAt
                by {
                  ...UserProfileUI
                }
              }
            }
            assetRules {
              id
              name
              evaluationCadence
              asset {
                ...AssetCardFields
              }
            }
            fxTradeLinks {
              id
              kind
              fxTrade {
                id
                externalId
                tradeType
                status
                sellCurrency
                buyCurrency
                sellToBuyRate
                sellAmount {
                  amount
                  currency
                }
                buyAmount {
                  amount
                  currency
                }
              }
            }
            description
            ...AdminTransferGroup
            ...OperationsTransferGroupActions
          }
        }
      }
    `),
    {
      variables: { id: transferGroupId },
    },
  );

  const transferGroup = data.node;
  if (!transferGroup || transferGroup.__typename !== "TransferGroup") {
    throw new NotFoundError();
  }

  useOpsTitle(`Transfer Group #${transferGroup.shortRef}`);

  const dateEntries: { label: string; date: Date | null | undefined }[] = [
    { label: "Created at", date: transferGroup.createdAt },
    { label: "Requested for", date: transferGroup.requestedForDate },
    {
      label: "Expected completion",
      date: transferGroup.expectedCompletionDate,
    },
    { label: "Actual completion", date: transferGroup.actualCompletionDate },
  ];

  return (
    <AdminPageContainer>
      <AdminPageHeader title="Transfer Group" />
      <BreadcrumbArray
        stack={[
          { title: "Transfer Groups", to: "/transfer-groups" },
          {
            title: <span className="font-mono">#{transferGroup.shortRef}</span>,
          },
        ]}
      />
      <div className="flex flex-col gap-6">
        <dl className="grid grid-cols-2 gap-4 rounded-md border p-4 md:grid-cols-4">
          <div className="flex flex-col gap-1">
            <dt>
              <Txt variant="detail-label">Organisation</Txt>
            </dt>
            <dd>
              <Txt variant="value">
                <OrganisationCard
                  organisation={transferGroup.organisation}
                  linkTo={`/organisations/${transferGroup.organisation.id}`}
                />
              </Txt>
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt>
              <Txt variant="detail-label">Created by</Txt>
            </dt>
            <dd>
              <Txt variant="value">
                {transferGroup.createdByUser ? (
                  <UserProfile {...transferGroup.createdByUser} />
                ) : (
                  "-"
                )}
              </Txt>
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt>
              <Txt variant="detail-label">Kind</Txt>
            </dt>
            <dd>
              <Txt variant="value">{prettyPrintEnum(transferGroup.kind)}</Txt>
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt>
              <Txt variant="detail-label">Status</Txt>
            </dt>
            <dd>
              {transferGroup.status && (
                <StatusUI status={transferGroup.status} />
              )}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt>
              <Txt variant="detail-label">Total volume</Txt>
            </dt>
            <dd>
              <Txt variant="value">
                {transferGroup.totalVolume ? (
                  <CurrencyAmountUI {...transferGroup.totalVolume} />
                ) : (
                  "-"
                )}
              </Txt>
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt>
              <Txt variant="detail-label">Description</Txt>
            </dt>
            <dd>
              <Txt variant="value">{transferGroup.description}</Txt>
            </dd>
          </div>
        </dl>

        <div className="flex flex-col gap-4 rounded-md border p-4 text-sm">
          <Txt variant="section-title">Timeline</Txt>
          <div>
            {dateEntries.map(({ label, date }) => (
              <DetailSubRow
                key={label}
                label={label}
                value={
                  date ? (
                    <DateUI date={date} variant="DATETIME" />
                  ) : (
                    <span className="text-tertiary">—</span>
                  )
                }
              />
            ))}
          </div>
        </div>

        {(transferGroup.approvalRequest ||
          transferGroup.userReviewRequired) && (
          <div className="flex flex-col gap-4 rounded-md border p-4 text-sm">
            <Txt variant="section-title">Approval</Txt>
            <div>
              <DetailSubRow
                label="Status"
                value={
                  transferGroup.approvalRequest ? (
                    <StatusUI status={transferGroup.approvalRequest.result} />
                  ) : (
                    <span className="text-tertiary">—</span>
                  )
                }
              />
              <DetailSubRow
                label="Review required"
                value={transferGroup.userReviewRequired ? "Yes" : "No"}
              />
              <DetailSubRow
                label="# required"
                value={
                  transferGroup.approvalRequest?.approvalPolicy
                    ?.numUserReviewsRequired ?? "Unknown"
                }
              />
              <DetailSubRow
                label="# approved"
                value={transferGroup.approvalRequest?.currentApprovedCount ?? 0}
              />
              <DetailSubRow
                label="Required by"
                value={
                  transferGroup.approvalReviewRequiredByDate ? (
                    <DateUI
                      date={transferGroup.approvalReviewRequiredByDate}
                      variant="DATETIME"
                    />
                  ) : (
                    <span className="text-tertiary">—</span>
                  )
                }
              />
            </div>
            {!!transferGroup.approvalRequest?.currentApprovalReviews
              ?.length && (
              <div className="flex flex-col gap-2">
                <Txt variant="detail-label">Reviews</Txt>
                <div className="flex flex-col gap-2">
                  {transferGroup.approvalRequest.currentApprovalReviews.map(
                    (review) => (
                      <div
                        key={review.id}
                        className="flex items-center justify-between gap-2"
                      >
                        <div className="flex items-center gap-2">
                          {review.by ? (
                            <UserProfile {...review.by} />
                          ) : (
                            <span className="text-tertiary">—</span>
                          )}
                          <StatusUI status={review.decision} />
                        </div>
                        <DateUI date={review.createdAt} variant="DATETIME" />
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {transferGroup.assetRules && transferGroup.assetRules.length > 0 && (
          <div className="flex flex-col gap-4 rounded-md border p-4 text-sm">
            <div className="font-semibold text-base">Rule</div>
            <div className="flex flex-col gap-3">
              {transferGroup.assetRules.map((assetRule) => (
                <div key={assetRule.id}>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-0.5">
                      <Link
                        to="/asset-rules/$ruleId"
                        params={{
                          ruleId: assetRule.id,
                        }}
                        className="font-medium hover:underline"
                      >
                        {assetRule.name}
                      </Link>
                      <Txt variant="caption" tone="secondary">
                        {prettyPrintEnum(assetRule.evaluationCadence)}
                      </Txt>
                    </div>
                  </div>
                  {assetRule.asset && (
                    <AccountCard
                      account={assetRule.asset}
                      subText="BUSINESS_ENTITY"
                      linkTo={`/assets/${assetRule.asset.id}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4 rounded-md border p-4 text-sm">
          <Txt variant="section-title">Actions</Txt>
          <PaneActions {...transferGroup} />
        </div>

        <div className="rounded-md border p-4">
          <div className="mb-2">
            <Txt variant="section-title">FX Trades</Txt>
          </div>
          {transferGroup.fxTradeLinks &&
          transferGroup.fxTradeLinks.length > 0 ? (
            <div className="divide-y divide-separator">
              {transferGroup.fxTradeLinks.map((link) => (
                <div
                  key={link.fxTrade?.id}
                  className="flex items-center justify-between py-2 first:pt-0 last:pb-0"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center rounded bg-secondary px-2 py-0.5 font-medium font-mono text-primary text-xs">
                      {link.kind}
                    </span>
                    {link.fxTrade && (
                      <div className="flex flex-col gap-0.5">
                        <Link
                          to={`/fx-trades/${link.fxTrade.id}`}
                          className="font-medium text-sm hover:underline"
                        >
                          {prettyPrintEnum(link.fxTrade.tradeType)} ·{" "}
                          {new Decimal(
                            link.fxTrade.sellAmount.amount,
                          ).toFixed()}{" "}
                          {link.fxTrade.sellCurrency} →{" "}
                          {new Decimal(link.fxTrade.buyAmount.amount).toFixed()}{" "}
                          {link.fxTrade.buyCurrency} (1{" "}
                          {link.fxTrade.sellCurrency} ={" "}
                          {new Decimal(link.fxTrade.sellToBuyRate).toFixed()}{" "}
                          {link.fxTrade.buyCurrency})
                        </Link>
                        {link.fxTrade.externalId && (
                          <span className="font-mono text-tertiary text-xs">
                            {link.fxTrade.externalId}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {link.fxTrade?.status && (
                      <StatusUI status={link.fxTrade.status} />
                    )}
                    <UnlinkFxTradeButton transferGroupId={transferGroup.id} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-tertiary">
              No FX trades linked to this transfer group.
            </p>
          )}
        </div>

        <TransferGroupUI
          transferGroup={transferGroup}
          currentFundMovementId={null}
        />
      </div>
    </AdminPageContainer>
  );
}

const UnlinkFxTradeButton: FC<{ transferGroupId: string }> = ({
  transferGroupId,
}) => {
  const [open, setOpen] = useState(false);

  const [unlinkMutation, { loading }] = useMutation(
    graphql(`
      mutation TransferGroupDetail_UnlinkFxTrade($input: UnlinkFxTradeFromTransferGroupInput!) {
        unlinkFxTradeFromTransferGroup(input: $input) {
          transferGroupId
        }
      }
    `),
    { refetchQueries: ["AdminTransferGroupDetail"] },
  );

  const handleConfirm = useCallback(async () => {
    await unlinkMutation({
      variables: { input: { transferGroupId } },
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "FX trade unlinked",
          description:
            "The FX trade has been unlinked from this transfer group.",
        });
        setOpen(false);
      },
      onError: (error) => {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to unlink FX trade",
          description:
            error.graphQLErrors?.[0]?.message ??
            "An unexpected error occurred.",
        });
      },
    });
  }, [transferGroupId, unlinkMutation]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-danger">
          Unlink
        </Button>
      </DialogTrigger>
      <DialogContent className="flex flex-col gap-4 sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Unlink FX trade</DialogTitle>
          <DialogDescription>
            Are you sure you want to unlink this FX trade from the transfer
            group?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={loading}
            onClick={handleConfirm}
          >
            {loading ? <Spinner /> : "Unlink"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
